import { z } from "zod";

// User-submitted feedback from the in-app form. Stored under a single
// admin-readable partition (pk="FEEDBACK", sk="<createdAt>#<id>") so
// the inbox is one Query away. The submitter's sub + email are
// stamped server-side from the JWT — clients never set them.
//
// Why one partition: feedback is typed by hand, so volume stays low.
// A scan over a single partition with composite sort (createdAt#id)
// is cheap and gives ordered reads for free; no GSI needed yet.
//
// Privacy: the body is user-typed prose and may contain incidental
// PII (their name, an exercise nickname, …). Logs that touch the
// feedback path log only sub + id, never the body.

// "crash" is written by the app itself (OP-33), never picked by a person: the
// feedback form leaves it out of its picker.
export const FeedbackKind = z.enum(["bug", "feature", "other", "crash"]);
export type FeedbackKind = z.infer<typeof FeedbackKind>;

export const FeedbackPriority = z.enum(["p0", "p1", "p2"]);
export type FeedbackPriority = z.infer<typeof FeedbackPriority>;

export const FeedbackStatus = z.enum([
  "open",
  "in_progress",
  "shipped",
  "wontfix",
]);
export type FeedbackStatus = z.infer<typeof FeedbackStatus>;

export const FeedbackSurface = z.enum(["mobile", "web"]);
export type FeedbackSurface = z.infer<typeof FeedbackSurface>;

// Fields the client supplies on POST /api/feedback. Server fills
// the rest (id, sub, email, status, priority, createdAt, updatedAt).
export const NewFeedback = z.object({
  kind: FeedbackKind,
  // Trimmed prose. 1..2000 covers a paragraph-ish report; longer
  // submissions probably belong in a Linear issue, not the inbox.
  body: z.string().trim().min(1).max(2000),
  surface: FeedbackSurface,
  // Stamped from package.json at submission time. Pinning the
  // version on the row lets us correlate "this happened in 1.23.2"
  // without asking the user.
  appVersion: z.string().min(1).max(32),
  // Optional device info (Android version + build / browser UA);
  // 128-char ceiling keeps the row small.
  device: z.string().max(128).optional(),
  // Optional last-active screen / route name (mobile: "LoggerScreen",
  // web: "WorkoutLog"). Helps narrow down repro paths.
  lastScreen: z.string().max(64).optional(),
  // Optional machine payload that does NOT belong in `body` (which is
  // human prose, capped at 2000). The toxic-draft recovery flow puts the
  // FULL quarantined draft JSON here — never truncated — so support can
  // inspect/replay the exact bytes that crashed (root-cause fix: a draft
  // is structured data, not a paragraph). PII-scrubbed client-side
  // (note/name stripped) but otherwise verbatim. Capped well under the
  // 400KB DynamoDB item limit; a real workout draft is a few KB.
  attachment: z.string().max(300_000).optional(),
  // #320: a crash row's grouping key, hashed on the device from version
  // code, exit reason, error class and first frame. Same crash, same key.
  fingerprint: z.string().regex(/^[0-9a-f]{8}$/).optional(),
});
export type NewFeedback = z.infer<typeof NewFeedback>;

export const Feedback = NewFeedback.extend({
  id: z.string().uuid(),
  // Both go to null when the submitter deletes their account (LEGAL-6).
  // The report itself is retained — the privacy notice says feedback
  // stays because it describes a defect of the PRODUCT, not a person —
  // so erasure strips the identity and keeps the text. Nullable rather
  // than absent: an omitted attribute stops the stored row parsing, and
  // every read path (admin PATCH, the inbox parsers) re-validates it.
  submitterSub: z.string().min(1).nullable(),
  submitterEmail: z.string().email().nullable(),
  status: FeedbackStatus,
  // Null until an admin triages. Inbox UI sorts (priority asc, then
  // createdAt desc) so triaged-urgent surfaces above the firehose.
  priority: FeedbackPriority.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Feedback = z.infer<typeof Feedback>;

// Admin-only PATCH payload. Both fields optional so an admin can set
// just priority, just status, or both in one call.
export const FeedbackPatch = z.object({
  priority: FeedbackPriority.nullable().optional(),
  status: FeedbackStatus.optional(),
});
export type FeedbackPatch = z.infer<typeof FeedbackPatch>;
